import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';

interface DiscoveredInstance {
	binary_path: string;
	config_path: string;
	version: string;
	running: boolean;
}

export default function DiscoverInstancesPage() {
	const [discovering, setDiscovering] = useState(false);
	const [instances, setInstances] = useState<DiscoveredInstance[]>([]);
	const [error, setError] = useState('');
	const navigate = useNavigate();

	const handleDiscover = async () => {
		setDiscovering(true);
		setError('');

		try {
			// Trigger discovery on agent
			const response = await apiClient.post<{ instances: DiscoveredInstance[] }>('/api/v1/instances/discover');
			setInstances(response.instances || []);
		} catch (err: any) {
			setError(err.message || 'Discovery failed');
		} finally {
			setDiscovering(false);
		}
	};

	const handleSecureInstance = async (instance: DiscoveredInstance) => {
		try {
			// Register and secure the instance
			await apiClient.post('/api/v1/instances/secure', {
				binary_path: instance.binary_path,
				config_path: instance.config_path,
			});

			// Redirect to dashboard
			navigate('/dashboard', {
				state: { message: 'Instance secured successfully!' },
			});
		} catch (err: any) {
			setError(err.message || 'Failed to secure instance');
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-3xl mx-auto">
				<div className="text-center mb-8">
					<h2 className="text-3xl font-bold text-gray-900 mb-2">
						Discover Existing AI Agents
					</h2>
					<p className="text-gray-600">
						Scan your system for existing AI agent installations and secure them
					</p>
				</div>

				<div className="bg-white shadow rounded-lg p-6 mb-6">
					<div className="text-center">
						<button
							onClick={handleDiscover}
							disabled={discovering}
							className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
						>
							{discovering ? 'Scanning...' : 'Scan for AI Workspaces'}
						</button>
					</div>

					{error && (
						<div className="mt-4 rounded-md bg-red-50 p-4">
							<div className="text-sm text-red-800">{error}</div>
						</div>
					)}
				</div>

				{instances.length > 0 && (
					<div className="bg-white shadow rounded-lg overflow-hidden">
						<div className="px-4 py-5 sm:px-6">
							<h3 className="text-lg font-medium text-gray-900">
								Discovered Instances ({instances.length})
							</h3>
						</div>
						<ul className="divide-y divide-gray-200">
							{instances.map((instance, index) => (
								<li key={index} className="px-4 py-4 sm:px-6">
									<div className="flex items-center justify-between">
										<div className="flex-1 min-w-0">
											<div className="flex items-center">
												<div className="flex-shrink-0">
													<div
														className={`h-10 w-10 rounded-full flex items-center justify-center ${
															instance.running
																? 'bg-green-100 text-green-600'
																: 'bg-gray-100 text-gray-600'
														}`}
													>
														{instance.running ? '✓' : '○'}
													</div>
												</div>
												<div className="ml-4">
													<p className="text-sm font-medium text-gray-900 truncate">
														{instance.binary_path}
													</p>
													<p className="text-sm text-gray-500">
														Version: {instance.version}
													</p>
													{instance.config_path && (
														<p className="text-xs text-gray-400">
															Config: {instance.config_path}
														</p>
													)}
												</div>
											</div>
										</div>
										<div className="ml-4 flex-shrink-0">
											<button
												onClick={() => handleSecureInstance(instance)}
												className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
											>
												Secure This Instance
											</button>
										</div>
									</div>
								</li>
							))}
						</ul>
					</div>
				)}

				{!discovering && instances.length === 0 && (
					<div className="bg-white shadow rounded-lg p-6">
						<div className="text-center text-gray-500">
							<p>No AI agent instances found on this system.</p>
							<p className="text-sm mt-2">
								Click "Scan" to search for installations, or{' '}
								<a href="/dashboard" className="text-primary-600 hover:text-primary-500">
									install a new instance
								</a>
								.
							</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
